import {
  AppendInvoiceHistoryRequest,
  InvoiceHistoryResponse,
} from '@weave/schema-gen-ts/dist/schemas/payments/views/service/service.pb';
import { http, APIResponse } from '@frontend/fetch';
import { formatSearchParams, formatExportInvoiceHistoryParams } from './format-helpers';
import { InvoiceSearchParams, InvoiceModel, InvoiceResponse, InvoiceDetailModel, InvoiceModelV3 } from './types';

const LOCATION_ID_HEADER = 'Location-Id';
const addLocationHeader = (locationId: string, headers: Record<string, string> = {}) => ({
  headers: { [LOCATION_ID_HEADER]: locationId, ...headers },
});

/**
 * Gets the invoice history and summary from the newer (10/5) invoice endpoint
 * @param url Url to use ie: `payments/v1/search/invoices`
 * @param searchParams The query string parameters for the request
 * @param options Additional options for the request
 */
export const getInvoiceHistory = async (url: string, searchParams: InvoiceSearchParams): Promise<InvoiceResponse> => {
  const formattedSearchParams = formatSearchParams(searchParams);
  return await http.get<InvoiceResponse>(url, {
    params: formattedSearchParams,
  });
};

/**
 * This is a wrapper around get invoice history that gets a single invoice
 * @param invoiceId The id of the invoice to retrieve
 */
export const getInvoice = (invoiceId: string, locationIds: (string | undefined)[]) =>
  http.getData<InvoiceDetailModel>(`payments/v1/invoices/${invoiceId}`, {
    params: { locationIds },
  });

export const getInvoiceActivity = (invoiceId: string, locationId: string) =>
  http.get<InvoiceHistoryResponse>(
    `payments/views/service/invoices/${invoiceId}/history`,
    addLocationHeader(locationId)
  );

export const updateInvoiceActivity = (invoiceId: string, locationId: string, data: AppendInvoiceHistoryRequest) =>
  http.post(`payments/views/service/invoices/${invoiceId}/history`, data, addLocationHeader(locationId));

type GetInvoiceReceiptResponse = string;
/**
 * Gets the receipt HTML for an invoice with a payment
 * @param invoice Invoice to get the receipt for
 */
export const getInvoiceReceipt = async (invoice: InvoiceModel) => {
  if (!invoice) return Promise.resolve(undefined);

  const response = http.get<string>(`payments/v1/invoices/${invoice.id}/receipt`, {
    headers: { 'Content-Type': 'text/html' },
    responseType: 'text',
  });

  return response;
};

export const exportInvoiceHistory = async (searchParams: InvoiceSearchParams) => {
  const params = formatSearchParams(formatExportInvoiceHistoryParams(searchParams));
  const response = http.get<Blob>(`payments/v1/search/invoices`, {
    headers: { Accept: 'text/csv' },
    responseType: 'blob',
    params,
  });
  return response;
};

export const viewInvoiceAttachment = (attachmentLink: string) => {
  const response = http.get<Blob>(attachmentLink, { responseType: 'blob' });
  return response;
};

/**
 * The sends a request to mark an invoice as recorded.
 * TODO: this should return the updated invoice
 * @param invoice Invoice to mark as recorded
 */
export const markInvoiceRecorded = async (paymentId?: string) => {
  if (!paymentId) {
    console.warn('attempted to record an invoice with no payment.');
    return Promise.resolve(undefined);
  }
  const response = http.post(`payments/v1/payments/${paymentId}/recorded`, undefined, {
    responseType: 'none',
  });
  return response;
};

/**
 * Removes the recorded status from an invoice
 * TODO: this should return the updated invoice
 * @param invoice Invoice to mark unrecorded
 */
export const markInvoiceUnrecorded = (paymentId?: string) => {
  if (!paymentId) return Promise.resolve(undefined);

  const response = http.delete(`payments/v1/payments/${paymentId}/recorded`, {
    responseType: 'none',
  });
  return response;
};

/**
 * Sends a request to mark an invoice as canceled
 * @param invoice Invoice to mark as canceled
 */
export const cancelInvoice = async (invoice: InvoiceModel) =>
  await http.post(`payments/v1/invoices/${invoice.id}/cancel`);

export const deleteInvoice = async (invoice: InvoiceModel) => await http.delete(`payments/requests/${invoice.id}`);

// REFUNDS

/**
 * Gets the receipt HTML for an payment refund
 */
export const getRefundReceipt = async (paymentId: string, stripeRefundId: string) => {
  return await http.get<GetInvoiceReceiptResponse>(
    `payments/v1/payments/${paymentId}/refunds/${stripeRefundId}/receipt`,
    {
      responseType: 'text',
    }
  );
};

type SendInvoicePayload = string[];

/**
 * Posts a request to send email receipts to the given emails
 * @param invoice Just need the invoice id
 * @param emails An array of emails
 */
export const sendInvoiceReceipt = ({
  invoiceId,
  emails,
  userId,
}: {
  invoiceId: string;
  emails: SendInvoicePayload;
  userId?: string;
}) =>
  http
    .post<APIResponse<null>>(`payments/v1/invoices/${invoiceId}/receipt`, {
      emails,
      userId,
    })
    .then((res) => res.data as null);

export const createPaymentRequest = async (formData: FormData, locationId: string) => {
  const response = await http.post<{ data: InvoiceModelV3 }>(
    `payments/v3/invoices`,
    formData,
    addLocationHeader(locationId)
  );
  return response?.data;
};
